import React, { useMemo } from "react";
import { Tag, Tooltip } from "antd";
import WeekCell from "./WeekCell";
import {
  buildWeekIndex,
  employeesOnSchedule,
  formatWeekLabel,
  afternoonWeekCountByEmployee,
} from "../utils/scheduleHelpers";
import { getSecondaryRoles } from "../../../utils/employeeRoles";

// Renders rows = employees, cols = weeks. Sticky first column for the
// employee name so the grid scrolls horizontally on narrow viewports.
function WeekGrid({
  schedule,
  employees,
  shiftNameById,
  onCellClick,
  onEmployeeClick,
  teamFilter, // optional array of team names; null/[] = show all
  excludedEmployeeIds, // optional Set/array of ids to dim + tag
}) {
  const weeks = schedule?.weeks || [];
  const afternoonShiftId = schedule?.afternoonShiftIdSnapshot;
  const employeeIndex = useMemo(() => buildWeekIndex(weeks), [weeks]);
  const allEmployees = useMemo(
    () => employeesOnSchedule(schedule, employees),
    [schedule, employees]
  );
  const excludedSet = useMemo(
    () => new Set(excludedEmployeeIds || []),
    [excludedEmployeeIds]
  );
  const employeeList = useMemo(() => {
    const base =
      !teamFilter || teamFilter.length === 0
        ? allEmployees
        : allEmployees.filter((e) =>
            new Set(teamFilter).has(e.employeeTeam || "_unteamed")
          );
    // Group rows: same team kept together, then same role together
    // inside the team, then alphabetical. Makes per-role afternoon
    // coverage trivial to scan because all e.g. "Take-Off · Flex
    // Tirana" employees are contiguous rows under one group header.
    return [...base].sort((a, b) => {
      const aTeam = a.employeeTeam || "_zzz";
      const bTeam = b.employeeTeam || "_zzz";
      if (aTeam !== bTeam) return aTeam.localeCompare(bTeam);
      const aRole = a.employeeRoleName || "_zzz";
      const bRole = b.employeeRoleName || "_zzz";
      if (aRole !== bRole) return aRole.localeCompare(bRole);
      return `${a.employeeLastName || ""} ${a.employeeFirstName || ""}`.localeCompare(
        `${b.employeeLastName || ""} ${b.employeeFirstName || ""}`
      );
    });
  }, [allEmployees, teamFilter]);
  // Pre-compute, for each row, whether it's the FIRST row of a new
  // (team, role) group so we can emit a separator header above it.
  const groupBoundaries = useMemo(() => {
    const out = new Map(); // employeeId -> { team, role, size }
    let i = 0;
    while (i < employeeList.length) {
      const e = employeeList[i];
      const team = e.employeeTeam || "_unteamed";
      const role = e.employeeRoleName || "—";
      let j = i;
      while (
        j < employeeList.length &&
        (employeeList[j].employeeTeam || "_unteamed") === team &&
        (employeeList[j].employeeRoleName || "—") === role
      ) {
        j += 1;
      }
      out.set(e.employeeId, { team, role, size: j - i });
      i = j;
    }
    return out;
  }, [employeeList]);
  const afternoonCounts = useMemo(
    () => afternoonWeekCountByEmployee(schedule),
    [schedule]
  );
  // For each (team, role) bucket, count how many of its members are
  // on afternoon EACH week — surfaces "Take-Off · Tirana has 2 SR + 1
  // jr this week" at a glance in the group header.
  const groupAfternoonByWeek = useMemo(() => {
    const out = new Map(); // `${team}|${role}|${weekStart}` -> count
    const empMeta = new Map(
      employeeList.map((e) => [
        e.employeeId,
        {
          team: e.employeeTeam || "_unteamed",
          role: e.employeeRoleName || "—",
        },
      ])
    );
    for (const w of weeks) {
      for (const a of w.assignments || []) {
        if (a.shiftId !== afternoonShiftId) continue;
        const meta = empMeta.get(a.employeeId);
        if (!meta) continue;
        const k = `${meta.team}|${meta.role}|${w.weekStart}`;
        out.set(k, (out.get(k) || 0) + 1);
      }
    }
    return out;
  }, [weeks, afternoonShiftId, employeeList]);

  if (employeeList.length === 0) {
    return (
      <div className="orari-grid__wrap" style={{ padding: 16 }}>
        Nuk ka punonjës të caktuar për këtë muaj. Krijo një politikë rotacioni
        dhe ekzekuto gjenerimin.
      </div>
    );
  }

  return (
    <div className="orari-grid__wrap">
      <table className="orari-grid__table">
        <thead>
          <tr>
            <th className="orari-grid__employee">Punonjësi</th>
            {weeks.map((w) => (
              <th key={w.weekStart}>
                <div>{formatWeekLabel(w)}</div>
                <small style={{ color: "#888", fontWeight: 400 }}>
                  {w.workingDays} ditë pune
                </small>
              </th>
            ))}
            <th>Pasdite</th>
          </tr>
        </thead>
        <tbody>
          {employeeList.map((emp) => {
            const row = employeeIndex.get(emp.employeeId) || {};
            const isExcluded = excludedSet.has(emp.employeeId);
            const groupHead = groupBoundaries.get(emp.employeeId);
            return (
              <React.Fragment key={emp.employeeId}>
                {groupHead ? (
                  <tr className="orari-grid__group-row">
                    <th className="orari-grid__group-cell" scope="rowgroup">
                      <span className="orari-grid__group-role">
                        {groupHead.role}
                      </span>
                      <span className="orari-grid__group-team">
                        {groupHead.team === "_unteamed"
                          ? "Pa ekip"
                          : groupHead.team}
                      </span>
                      <Tag style={{ marginLeft: 6 }}>{groupHead.size}</Tag>
                    </th>
                    {weeks.map((w) => {
                      const count =
                        groupAfternoonByWeek.get(
                          `${groupHead.team}|${groupHead.role}|${w.weekStart}`
                        ) || 0;
                      return (
                        <td
                          key={w.weekStart}
                          className="orari-grid__group-week"
                        >
                          {count > 0 ? (
                            <Tooltip
                              title={`${count} në pasdite këtë javë`}
                            >
                              <span className="orari-grid__group-count">
                                {count}
                              </span>
                            </Tooltip>
                          ) : (
                            <span className="orari-grid__group-count orari-grid__group-count--zero">
                              0
                            </span>
                          )}
                        </td>
                      );
                    })}
                    <td className="orari-grid__group-week" />
                  </tr>
                ) : null}
              <tr
                style={{ opacity: isExcluded ? 0.5 : 1 }}
              >
                <td
                  className="orari-grid__employee"
                  style={{ cursor: onEmployeeClick ? "pointer" : "default" }}
                  onClick={() => onEmployeeClick?.(emp)}
                >
                  <div style={{ fontWeight: 500 }}>
                    {emp.employeeFirstName} {emp.employeeLastName}
                    {isExcluded ? (
                      <Tag color="orange" style={{ marginLeft: 6 }}>Përjashtuar</Tag>
                    ) : null}
                  </div>
                  <small style={{ color: "#888" }}>
                    {emp.employeeRoleName || "—"}
                    {(() => {
                      const others = getSecondaryRoles(emp);
                      if (!others.length) return null;
                      return (
                        <Tooltip
                          title={`Mbulon edhe: ${others.join(", ")}`}
                        >
                          <Tag
                            color="blue"
                            style={{ marginLeft: 6, fontSize: 10 }}
                          >
                            +{others.length}
                          </Tag>
                        </Tooltip>
                      );
                    })()}
                    {emp.employeeTeam ? ` · ${emp.employeeTeam}` : ""}
                  </small>
                </td>
                {weeks.map((w) => (
                  <td key={w.weekStart}>
                    <WeekCell
                      assignment={row[w.weekStart]}
                      afternoonShiftId={afternoonShiftId}
                      shiftNameById={shiftNameById}
                      onClick={onCellClick}
                      weekStart={w.weekStart}
                      employeeId={emp.employeeId}
                    />
                  </td>
                ))}
                <td>
                  <Tag color={afternoonCounts[emp.employeeId] ? "red" : "default"}>
                    {afternoonCounts[emp.employeeId] || 0}
                  </Tag>
                </td>
              </tr>
              </React.Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Bypassed / controller employees: shown as a separate footer
          strip so admins know who is intentionally outside the
          rotation, without polluting the rotation roster above. */}
      {Array.isArray(schedule?.fairnessReport?.bypassed) &&
      schedule.fairnessReport.bypassed.length > 0 ? (
        <div className="orari-grid__bypass">
          <span className="orari-grid__bypass-label">
            Jashtë rotacionit (mbikëqyrës / kontrollor):
          </span>
          {schedule.fairnessReport.bypassed.map((b) => (
            <Tooltip
              key={b.employeeId}
              title={
                b.reason === "role_excluded"
                  ? "Roli i tij/saj është caktuar si i përjashtuar nga politika"
                  : b.reason === "month_override"
                    ? "Përjashtuar vetëm për këtë muaj nga parametrat e muajit"
                    : "Përjashtuar nga lista e politikës"
              }
            >
              <Tag color="purple">
                {b.employeeName}
                <span style={{ opacity: 0.7, marginLeft: 4 }}>
                  · {b.roleName || "—"}
                  {b.team && b.team !== "_unteamed" ? ` · ${b.team}` : ""}
                </span>
              </Tag>
            </Tooltip>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default WeekGrid;
